import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  Alert,
  CircularProgress,
  IconButton,
  useTheme,
  useMediaQuery,
  Paper,
  Fade,
  Slide,
  TextField,
  InputAdornment,
  Divider,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Payment as PaymentIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Print as PrintIcon,
  Undo as UndoIcon,
  Cancel as CancelIcon,
  ContentCopy as ContentCopyIcon,
  ReceiptLong as ReceiptLongIcon,
  Send as SendIcon,
  CreditCard as CreditCardIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  History as HistoryIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { Invoice, Log } from '../../types';
import invoiceService from '../../services/invoiceService';
import paymentService, { PaymentSummary } from '../../services/paymentService';
import clienteCreditoService from '../../services/clienteCreditoService';
import logService from '../../services/logService';
import { formatarData, formatarDataHora, formatarValor } from '../../utils/formatToView';
import Swal from 'sweetalert2';
import { InvoiceStatusChip } from '../../utils/faturaStatusUtils';
import EnviarInvoiceModal from './EnviarInvoiceModal';

interface InvoiceViewModalProps {
  open: boolean;
  onClose: () => void;
  invoice_id: number;
  onInvoiceUpdated?: () => Promise<void> | void;
  //onEdit?: (invoice: Invoice) => void;
  onReceivePayment?: (invoice: Invoice) => void;
  onPrintSingleInvoiceWithSweet?: (invoice: Invoice) => void;
  //onCancelInvoice?: (invoice: number[]) => void;
  onEmitirNfcom?: (invoice: Invoice) => void;
  onProcessBoleto?: (invoice: Invoice) => void;
}

/* ─── tiny helper components ─────────────────────────────────────────────── */

const InfoRow = ({
  label,
  value,
  icon,
  highlight,
}: {
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
  highlight?: 'success' | 'error' | 'warning' | 'info';
}) => {
  const theme = useTheme();
  const colors: Record<string, string> = {
    success: theme.palette.success.main,
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
  };
  return (
    <Box
      display="flex"
      alignItems="flex-start"
      gap={0.75}
      sx={{ py: 0.35 }}
    >
      {icon && (
        <Box sx={{ color: 'text.disabled', mt: '1px', lineHeight: 0 }}>{icon}</Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          component="span"
          sx={{ fontSize: '0.68rem', fontWeight: 600, color: 'text.secondary', letterSpacing: 0.3, textTransform: 'uppercase' }}
        >
          {label}
          {': '}
        </Typography>
        <Typography
          component="span"
          sx={{
            fontSize: '0.75rem',
            fontWeight: highlight ? 700 : 500,
            color: highlight ? colors[highlight] : 'text.primary',
            wordBreak: 'break-word',
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

const SectionCard = ({
  icon,
  iconColor,
  title,
  action,
  children,
}: {
  icon: React.ReactNode;
  iconColor: string;
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        borderRadius: '10px',
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* card header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 1.5,
          py: 0.9,
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box display="flex" alignItems="center" gap={0.75}>
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: '6px',
              bgcolor: iconColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              lineHeight: 0,
            }}
          >
            {icon}
          </Box>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: 'text.secondary' }}>
            {title}
          </Typography>
        </Box>
        {action}
      </Box>
      {/* card body */}
      <Box sx={{ px: 1.5, py: 1.25 }}>{children}</Box>
    </Box>
  );
};

const StatBadge = ({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: 'default' | 'success' | 'warning' | 'error' | 'info';
}) => {
  const theme = useTheme();
  const palette = color === 'default' ? null : theme.palette[color];
  return (
    <Box
      sx={{
        flex: 1,
        borderRadius: '8px',
        border: `1px solid ${palette ? palette.main : theme.palette.divider}`,
        bgcolor: palette
          ? theme.palette.mode === 'dark'
            ? `${palette.main}18`
            : `${palette.main}10`
          : theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.04)'
            : 'rgba(0,0,0,0.02)',
        px: 1,
        py: 0.75,
        textAlign: 'center',
      }}
    >
      <Typography
        sx={{
          fontSize: '0.6rem',
          fontWeight: 700,
          letterSpacing: 0.5,
          textTransform: 'uppercase',
          color: palette ? palette.main : 'text.secondary',
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: '0.8rem',
          fontWeight: 800,
          color: palette ? palette.main : 'text.primary',
          letterSpacing: -0.3,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

/* ─── main component ──────────────────────────────────────────────────────── */

const InvoiceViewModal: React.FC<InvoiceViewModalProps> = ({
  open,
  onClose,
  invoice_id,
  onInvoiceUpdated,
  //onEdit,
  onReceivePayment,
  onPrintSingleInvoiceWithSweet,
  //onCancelInvoice,
  onEmitirNfcom,
  onProcessBoleto,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [paymentSummary, setPaymentSummary] = useState<PaymentSummary | null>(null);
  const [creditosUtilizados, setCreditosUtilizados] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [estornando, setEstornando] = useState<number | null>(null);
  const [enviarModalOpen, setEnviarModalOpen] = useState(false);
  const [showPayments, setShowPayments] = useState(true);
  const [showCredits, setShowCredits] = useState(true);
  const [logs, setLogs] = useState<Log[]>([]);
  const [showLogs, setShowLogs] = useState(true);

  useEffect(() => {
    if (open) {
      const style = document.createElement('style');
      style.textContent = `.swal2-container{z-index:9999!important}.swal2-popup{z-index:10000!important}`;
      document.head.appendChild(style);
      return () => { document.head.removeChild(style); };
    }
  }, [open]);

  useEffect(() => {
    const load = async () => {
      if (!invoice_id || !open) {
        setInvoice(null); setPaymentSummary(null); setCreditosUtilizados([]); setLogs([]); return;
      }
      try {
        setLoading(true);
        const res = await invoiceService.getInvoiceById(invoice_id);
        if (res.success && res.data) {
          setInvoice(res.data);
          const summary = await paymentService.getPaymentSummary(
            res.data.id, Number(res.data.valor), Number(res.data.acrescimo), Number(res.data.desconto)
          );
          setPaymentSummary(summary);
          try {
            const creditos = await clienteCreditoService.getCreditosUtilizados(res.data.id);
            setCreditosUtilizados(creditos);
          } catch { setCreditosUtilizados([]); }
          try {
            const resLogs = await logService.getLogsByEntity('fatura', res.data.id);
            if (resLogs.success && resLogs.data) {
              setLogs(resLogs.data.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
            } else { setLogs([]); }
          } catch { setLogs([]); }
        } else { setInvoice(null); setPaymentSummary(null); setCreditosUtilizados([]); setLogs([]); }
      } catch { setInvoice(null); setPaymentSummary(null); setCreditosUtilizados([]); setLogs([]); }
      finally { setLoading(false); }
    };
    load();
  }, [invoice_id, open]);

  if (!invoice && !loading && !open) return null;

  const getTotalAmount = () =>
    Number(invoice?.valor ?? 0) + Number(invoice?.acrescimo ?? 0) - Number(invoice?.desconto ?? 0);

  const isOverdue = () => {
    if (!invoice || invoice.status === 'pago' || invoice.status === 'cancelado') return false;
    return new Date(invoice.data_vencimento) < new Date();
  };

  const getDaysOverdue = () => {
    if (!invoice || !isOverdue()) return 0;
    return Math.floor((new Date().getTime() - new Date(invoice.data_vencimento).getTime()) / 86400000);
  };

  const canProcessBoleto = () => {
    if (!invoice) return false;
    const today = new Date();
    const dueDate = new Date(invoice.data_vencimento + 'T23:59:59');
    return invoice.status === 'aberto' && dueDate >= today;
  };

  const handleCopyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      Swal.fire({ title: 'Copiado!', text: `${fieldName} copiado`, icon: 'success', timer: 1800, showConfirmButton: false, toast: true, position: 'top-end' });
    } catch {
      Swal.fire({ title: 'Erro!', text: 'Não foi possível copiar', icon: 'error', timer: 1800, showConfirmButton: false, toast: true, position: 'top-end' });
    }
  };

  const handleEstornarPagamento = async (payment: any) => {
    try {
      const result = await Swal.fire({
        title: 'Confirmar Estorno',
        html: `<div style="text-align:left;font-size:0.875rem">
          <p><b>Valor:</b> ${formatarValor(Number(payment.valor_pago))}</p>
          <p><b>Forma:</b> ${payment.forma_pagamento}</p>
          <p><b>Data:</b> ${formatarData(payment.data_pagamento)}</p>
          <p style="color:#d32f2f;margin-top:8px"><b>⚠️ Esta ação é irreversível.</b></p>
        </div>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d32f2f',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Estornar',
        cancelButtonText: 'Cancelar',
        reverseButtons: true,
      });
      if (!result.isConfirmed) return;
      setEstornando(payment.id);
      await invoiceService.estornarPagamento(payment.id);
      Swal.fire({ title: 'Sucesso!', text: 'Pagamento estornado.', icon: 'success', timer: 2000, showConfirmButton: false });
      if (invoice_id) {
        const res = await invoiceService.getInvoiceById(invoice_id);
        if (res.success && res.data) {
          setInvoice(res.data);
          const summary = await paymentService.getPaymentSummary(res.data.id, Number(res.data.valor), Number(res.data.acrescimo), Number(res.data.desconto));
          setPaymentSummary(summary);
          try {
            const creditos = await clienteCreditoService.getCreditosUtilizados(res.data.id);
            setCreditosUtilizados(creditos);
          } catch { setCreditosUtilizados([]); }
          try {
            const resLogs = await logService.getLogsByEntity('fatura', res.data.id);
            if (resLogs.success && resLogs.data) {
              setLogs(resLogs.data.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
            } else { setLogs([]); }
          } catch { setLogs([]); }
        }
      }
      if (onInvoiceUpdated) {
        await onInvoiceUpdated();
      }
    } catch (err: any) {
      Swal.fire({ title: 'Erro!', text: err.response?.data?.message || 'Erro ao estornar', icon: 'error', confirmButtonText: 'OK' });
    } finally { setEstornando(null); }
  };

  const getCreditoAbatido = (c: any) =>
    Number(c.valor_aplicado ?? (Number(c.valor ?? 0) - Number(c.saldo ?? 0)));

  const totalCreditosAbatidos = creditosUtilizados.reduce((s, c) => s + getCreditoAbatido(c), 0);
  const totalQuitado = (paymentSummary?.totalPaid || 0) + totalCreditosAbatidos;
  const remainingAmountEfetivo = Math.max(0, (paymentSummary?.totalAmount || 0) - totalQuitado);
  const isPaidEfetivo = invoice?.status === 'pago' || remainingAmountEfetivo <= 0.001;
  const isPartiallyPaidEfetivo = !isPaidEfetivo && totalQuitado > 0;

  const paidPct = paymentSummary && paymentSummary.totalAmount > 0
    ? Math.min(100, (totalQuitado / paymentSummary.totalAmount) * 100)
    : 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      TransitionComponent={Slide}
      transitionDuration={250}
      PaperProps={{
        sx: {
          margin: isMobile ? 0 : 2,
          maxHeight: isMobile ? '100vh' : '88vh',
          borderRadius: isMobile ? 0 : '14px',
          bgcolor: theme.palette.mode === 'dark' ? '#0f1117' : '#f5f7fa',
          boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* ── HEADER ─────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          background:
            theme.palette.mode === 'dark'
              ? `linear-gradient(135deg, #1a1f35 0%, #0f1117 100%)`
              : `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          px: isMobile ? 2 : 2.5,
          pt: 2,
          pb: 1.5,
          position: 'relative',
          flexShrink: 0,
        }}
      >
        {/* decorative circle */}
        <Box
          sx={{
            position: 'absolute', right: -30, top: -30,
            width: 120, height: 120, borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.05)', pointerEvents: 'none',
          }}
        />
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 38, height: 38, borderRadius: '10px',
                bgcolor: 'rgba(255,255,255,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backdropFilter: 'blur(8px)',
              }}
            >
              <ReceiptIcon sx={{ fontSize: 20, color: '#fff' }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.6)', letterSpacing: 1, textTransform: 'uppercase' }}>
                Fatura
              </Typography>
              <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: '#fff', letterSpacing: -0.5, lineHeight: 1.2 }}>
                #{invoice?.id ?? '—'}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: 'rgba(255,255,255,0.7)', mt: '-2px',
              '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* progress bar when partially paid */}
        {paymentSummary && totalQuitado > 0 && !isPaidEfetivo && (
          <Box sx={{ mt: 1.5 }}>
            <Box display="flex" justifyContent="space-between" mb={0.4}>
              <Typography sx={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.55)', fontWeight: 600 }}>
                Pagamento parcial
              </Typography>
              <Typography sx={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.75)', fontWeight: 700 }}>
                {paidPct.toFixed(0)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={paidPct}
              sx={{
                height: 4, borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.15)',
                '& .MuiLinearProgress-bar': { bgcolor: theme.palette.success.light, borderRadius: 2 },
              }}
            />
          </Box>
        )}
      </Box>

      {/* ── BODY ───────────────────────────────────────────────────────────── */}
      <DialogContent
        sx={{
          px: isMobile ? 1.5 : 2,
          py: 1.5,
          flex: 1,
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { borderRadius: 4, bgcolor: 'divider' },
        }}
      >
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6} gap={1.5}>
            <CircularProgress size={32} thickness={4} />
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Carregando fatura…</Typography>
          </Box>
        ) : invoice ? (
          <Fade in timeout={250}>
            <Grid container spacing={1.25}>

              {/* Cliente */}
              <Grid item xs={12} sm={6}>
                <SectionCard icon={<PersonIcon sx={{ fontSize: 13 }} />} iconColor={theme.palette.primary.main} title="Cliente">
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                    {invoice.cliente?.nome ?? `ID ${invoice.cliente_id}`}
                  </Typography>
                  {invoice.cliente && (
                    <Box>
                      <InfoRow label="CPF/CNPJ" value={invoice.cliente.cpf_cnpj} />
                      <InfoRow
                        label="Endereço"
                        value={`${invoice.cliente.endereco}, ${invoice.cliente.bairro}`}
                      //icon={<LocationIcon sx={{ fontSize: 12 }} />}
                      />
                      <InfoRow label="Cidade" value={`${invoice.cliente.cidade} - ${invoice.cliente.estado} · CEP ${invoice.cliente.cep}`} />
                    </Box>
                  )}
                </SectionCard>
              </Grid>

              {/* Contrato */}
              <Grid item xs={12} sm={6}>
                <SectionCard icon={<BusinessIcon sx={{ fontSize: 13 }} />} iconColor={theme.palette.secondary.main} title="Contrato">
                  {invoice.contrato ? (
                    <>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'secondary.main', mb: 0.5 }}>
                        {invoice.contrato.titulo}
                      </Typography>
                      <InfoRow
                        label="Data"
                        value={formatarData(invoice.contrato.data_contrato)}
                      //icon={<CalendarIcon sx={{ fontSize: 12 }} />}
                      />
                      <InfoRow
                        label="Vencimento"
                        value={`Dia ${invoice.contrato.dia_vencimento}`}
                      //icon={<ScheduleIcon sx={{ fontSize: 12 }} />}
                      />
                      <InfoRow
                        label="Modalidade"
                        value={invoice.contrato.modalidade === 'pre-pago' ? 'Pré-pago' : 'Pós-pago'}
                      //icon={<CreditCardIcon sx={{ fontSize: 12 }} />}
                      />
                    </>
                  ) : (
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      {invoice.contrato_id ? `Contrato ID: ${invoice.contrato_id}` : 'Fatura Geral'}
                    </Typography>
                  )}
                </SectionCard>
              </Grid>

              {/* Detalhes */}
              <Grid item xs={12} sm={6}>
                <SectionCard icon={<CalendarIcon sx={{ fontSize: 13 }} />} iconColor={theme.palette.info.main} title="Detalhes">

                  <InfoRow
                    label="Tipo"
                    value={
                      <>
                        <Chip
                          label={invoiceService.formatType(invoice.tipo)}
                          size="small"
                          color="info"
                          variant="outlined"
                          sx={{ fontSize: '0.65rem', height: 18, ml: 0.25, mr: 1 }}
                        />
                        <InvoiceStatusChip status={invoice.status} size="small" />
                      </>
                    }
                  />
                  {invoice.data_cancelamento && <InfoRow label="Cancelado em" value={formatarDataHora(invoice.data_cancelamento)} />}
                  <InfoRow label="Criado em" value={formatarDataHora(invoice.createdAt)} />
                  {invoice.mes_referencia && (
                    <InfoRow
                      label="Referência"
                      value={(() => {
                        const [ano, mes] = invoice.mes_referencia.split('-');
                        return `${mes}/${ano}`;
                      })()}
                    />
                  )}
                  {invoice.data_inicial && invoice.data_final && (
                    <InfoRow label="Período" value={`${formatarData(invoice.data_inicial)} → ${formatarData(invoice.data_final)}`} />
                  )}
                  <InfoRow label="Vencimento" value={formatarData(invoice.data_vencimento)} highlight={isOverdue() ? 'error' : undefined} />
                  {isOverdue() && (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: theme.palette.error.light, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: theme.palette.error.light }}>
                        Vencida há {getDaysOverdue()} dia{getDaysOverdue() !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  )}
                  {invoice.data_pagamento && (
                    <InfoRow label="Pago em" value={formatarDataHora(invoice.data_pagamento)} highlight="success" />
                  )}
                  {invoice.descricao && <InfoRow label="Descrição" value={invoice.descricao} />}

                  {/* Linha Digitável */}
                  {invoice.linha_digitavel && (
                    <Box mt={1}>
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.4 }}>
                        Linha Digitável
                      </Typography>
                      <TextField
                        fullWidth size="small" value={invoice.linha_digitavel}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title="Copiar">
                                <IconButton size="small" onClick={() => invoice.linha_digitavel && handleCopyToClipboard(invoice.linha_digitavel, 'Linha Digitável')} sx={{ color: 'primary.main' }}>
                                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontSize: '0.7rem',
                            borderRadius: '7px',
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)',
                          },
                        }}
                      />
                    </Box>
                  )}

                  {/* QR Code Pix */}
                  {invoice.qr_code_pix && (
                    <Box mt={0.75}>
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.4 }}>
                        QR Code Pix
                      </Typography>
                      <TextField
                        fullWidth size="small" value={invoice.qr_code_pix}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title="Copiar">
                                <IconButton size="small" onClick={() => invoice.qr_code_pix && handleCopyToClipboard(invoice.qr_code_pix, 'QR Code Pix')} sx={{ color: 'primary.main' }}>
                                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontSize: '0.7rem',
                            borderRadius: '7px',
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)',
                          },
                        }}
                      />
                    </Box>
                  )}
                </SectionCard>
              </Grid>

              {/* Valores */}
              <Grid item xs={12} sm={6}>
                <SectionCard icon={<PaymentIcon sx={{ fontSize: 13 }} />} iconColor={theme.palette.success.main} title="Valores">
                  <Box
                    sx={{
                      borderRadius: '8px',
                      border: `1px solid ${theme.palette.divider}`,
                      overflow: 'hidden',
                      mb: 1,
                    }}
                  >
                    {/* value rows */}
                    {[
                      { label: 'Valor Base', value: formatarValor(Number(invoice.valor)), border: true },
                      ...(Number(invoice.acrescimo) > 0
                        ? [{ label: '+ Acréscimo', value: formatarValor(Number(invoice.acrescimo)), color: 'success.main', border: true }]
                        : []),
                      ...(Number(invoice.desconto) > 0
                        ? [{ label: '− Desconto', value: formatarValor(Number(invoice.desconto)), color: 'error.main', border: true }]
                        : []),
                    ].map((row, i, arr) => (
                      <Box
                        key={i}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          px: 0.6, py: 0.6,
                          borderBottom: i < arr.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                        }}
                      >
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{row.label}</Typography>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: (row as any).color ?? 'text.primary' }}>
                          {row.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Total highlight */}
                  <Box
                    sx={{
                      borderRadius: '8px',
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}18 0%, ${theme.palette.primary.light}10 100%)`,
                      border: `1.5px solid ${theme.palette.primary.main}40`,
                      px: 0.6, py: 0.6,
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}
                  >
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Total a Pagar
                    </Typography>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: 'primary.main', letterSpacing: -0.5 }}>
                      {formatarValor(getTotalAmount())}
                    </Typography>
                  </Box>
                </SectionCard>
                {/* Observações */}
                {invoice.observacoes && (
                  <>
                    <Box sx={{ mt: 1 }} />
                    <SectionCard icon={<InfoIcon sx={{ fontSize: 13 }} />} iconColor={theme.palette.info.main} title="Observações">
                      <Box
                        sx={{
                          overflow: 'hidden',
                          mb: 1,
                          p: 0
                        }}
                      >
                        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-line' }}>{invoice.observacoes}</Typography>
                      </Box>
                    </SectionCard>
                  </>
                )}
              </Grid>



              {/* Créditos Utilizados */}
              {creditosUtilizados.length > 0 && (
                <Grid item xs={12}>
                  <SectionCard
                    icon={<TrendingDownIcon sx={{ fontSize: 13 }} />}
                    iconColor={theme.palette.success.dark}
                    title={`Créditos Utilizados (${creditosUtilizados.length})`}
                    action={
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <Chip label="100% Pago" color="success" size="small" sx={{ fontSize: '0.62rem', height: 18, fontWeight: 700 }} />
                        <IconButton size="small" onClick={() => setShowCredits(v => !v)} sx={{ color: 'text.secondary' }}>
                          {showCredits ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </Box>
                    }
                  >
                    {/* summary stats */}
                    <Box display="flex" gap={1} mb={showCredits && creditosUtilizados.length > 0 ? 1.25 : 0}>
                      <StatBadge
                        label="Total Créditos"
                        value={formatarValor(creditosUtilizados.reduce((s, c) => s + Number(c.valor ?? c.valor_aplicado ?? 0), 0))}
                        color="success"
                      />
                      <StatBadge
                        label="Utilizados"
                        value={formatarValor(totalCreditosAbatidos)}
                        color="info"
                      />
                    </Box>

                    {showCredits && (
                      <Box>
                        {creditosUtilizados.map((credito) => {
                          const valAbatido = getCreditoAbatido(credito);
                          return (
                            <Box
                              key={credito.id}
                              sx={{
                                mb: 0.75,
                                borderRadius: '8px',
                                border: `1px solid ${theme.palette.divider}`,
                                px: 1.25, py: 1,
                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                              }}
                            >
                              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.4}>
                                <Box>
                                  <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: 'primary.main' }}>
                                    {clienteCreditoService.formatTipoCredito(credito.tipo)} · Nível {credito.nivel}
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                    {credito.cliente_origem?.nome ?? 'Sistema'} · {credito.createdAt ? new Date(credito.createdAt).toLocaleDateString('pt-BR') : '—'}
                                  </Typography>
                                </Box>
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: 'success.main' }}>
                                  {formatarValor(valAbatido)}
                                </Typography>
                              </Box>
                              <Box display="flex" gap={1.5}>
                                <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                  <b>Original:</b> {formatarValor(Number(credito.valor ?? valAbatido))}
                                </Typography>
                                <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                  <b>Saldo:</b> {formatarValor(Number(credito.saldo ?? 0))}
                                </Typography>
                              </Box>
                              {credito.observacoes && (
                                <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', mt: 0.3 }}>
                                  <b>Obs:</b> {credito.observacoes}
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </SectionCard>
                </Grid>
              )}

              {/* Histórico de Pagamentos */}
              {paymentSummary && (paymentSummary.payments.length > 0 || totalCreditosAbatidos > 0) && (
                <Grid item xs={12}>
                  <SectionCard
                    icon={<TrendingUpIcon sx={{ fontSize: 13 }} />}
                    iconColor={
                      isPaidEfetivo
                        ? theme.palette.success.main
                        : isPartiallyPaidEfetivo
                          ? theme.palette.warning.main
                          : theme.palette.grey[500]
                    }
                    title={`Pagamentos (${paymentSummary.payments.length})`}
                    action={
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <Chip
                          label={isPaidEfetivo ? 'Paga' : isPartiallyPaidEfetivo ? 'Parcial' : 'Em Aberto'}
                          color={isPaidEfetivo ? 'success' : isPartiallyPaidEfetivo ? 'warning' : 'default'}
                          size="small"
                          sx={{ fontSize: '0.62rem', height: 18, fontWeight: 700 }}
                        />
                        <IconButton size="small" onClick={() => setShowPayments(v => !v)} sx={{ color: 'text.secondary' }}>
                          {showPayments ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </Box>
                    }
                  >
                    {/* stats row */}
                    <Box display="flex" gap={1} mb={showPayments ? 1.25 : 0}>
                      <StatBadge label="Total" value={formatarValor(paymentSummary.totalAmount)} color="default" />
                      <StatBadge label="Pago" value={formatarValor(paymentSummary.totalPaid)} color="success" />
                      <StatBadge label="Créditos" value={formatarValor(totalCreditosAbatidos)} color="info" />
                      <StatBadge
                        label="Restante"
                        value={formatarValor(remainingAmountEfetivo)}
                        color={remainingAmountEfetivo > 0.001 ? 'warning' : 'success'}
                      />
                    </Box>

                    {showPayments && (
                      <Box>
                        {paymentSummary.payments.map((payment) => (
                          <Box
                            key={payment.id}
                            sx={{
                              mb: 0.75,
                              borderRadius: '8px',
                              border: `1px solid ${theme.palette.divider}`,
                              px: 1.25,
                              pt: 0.9,
                              pb: 0.75,
                              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                            }}
                          >
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                              <Box>
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.primary' }}>
                                  {payment.forma_pagamento}
                                </Typography>
                                <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                  {formatarDataHora(payment.data_pagamento)}
                                </Typography>
                                {(payment.coletor?.nome || payment.usuarioId_coletor) && (
                                  <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', mt: 0.15 }}>
                                    <b>Coletor:</b>{' '}
                                    {payment.coletor?.nome ??
                                      (payment.usuarioId_coletor != null ? `#${payment.usuarioId_coletor}` : '—')}
                                  </Typography>
                                )}
                              </Box>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: 'success.main' }}>
                                  {formatarValor(Number(payment.valor_pago))}
                                </Typography>
                                <Tooltip title="Estornar pagamento">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEstornarPagamento(payment)}
                                      disabled={estornando === payment.id}
                                      sx={{
                                        color: 'error.main',
                                        p: 0.4,
                                        borderRadius: '6px',
                                        '&:hover': { bgcolor: 'error.main', color: '#fff' },
                                      }}
                                    >
                                      {estornando === payment.id
                                        ? <CircularProgress size={14} color="inherit" />
                                        : <UndoIcon sx={{ fontSize: 14 }} />}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </Box>

                            {/* extras */}
                            {((Number(payment.acrescimo) || 0) > 0 ||
                              (Number(payment.desconto) || 0) > 0 ||
                              payment.motivo_desconto ||
                              (payment as any).motivoDesconto?.descricao ||
                              payment.transacao_id ||
                              payment.observacoes) && (
                                <Box
                                  mt={0.6}
                                  pt={0.6}
                                  sx={{ borderTop: `1px dashed ${theme.palette.divider}` }}
                                  display="flex"
                                  flexWrap="wrap"
                                  gap={1}
                                >
                                  {(Number(payment.acrescimo) || 0) > 0 && (
                                    <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                      <b>Acréscimo:</b> {formatarValor(Number(payment.acrescimo))}
                                    </Typography>
                                  )}
                                  {(Number(payment.desconto) || 0) > 0 && (
                                    <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                      <b>Desconto:</b> {formatarValor(Number(payment.desconto))}
                                    </Typography>
                                  )}
                                  {(payment.motivo_desconto || (payment as any).motivoDesconto?.descricao) && (
                                    <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                      <b>Motivo:</b> {payment.motivo_desconto || (payment as any).motivoDesconto?.descricao}
                                    </Typography>
                                  )}
                                  {payment.transacao_id && (
                                    <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                      <b>ID:</b> {payment.transacao_id}
                                    </Typography>
                                  )}
                                  {payment.observacoes && (
                                    <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                      <b>Obs:</b> {payment.observacoes}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </SectionCard>
                </Grid>
              )}

              {/* Logs da Fatura */}
              {logs.length > 0 && (
                <Grid item xs={12}>
                  <SectionCard
                    icon={<HistoryIcon sx={{ fontSize: 13 }} />}
                    iconColor={theme.palette.info.main}
                    title={`Histórico da Fatura (${logs.length})`}
                    action={
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <IconButton size="small" onClick={() => setShowLogs(v => !v)} sx={{ color: 'text.secondary' }}>
                          {showLogs ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </Box>
                    }
                  >
                    {showLogs && (
                      <Box>
                        {logs.map((log) => (
                          <Box
                            key={log.id}
                            sx={{
                              mb: 0.75,
                              borderRadius: '8px',
                              border: `1px solid ${theme.palette.divider}`,
                              px: 1.25,
                              pt: 0.9,
                              pb: 0.75,
                              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                            }}
                          >
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                              <Box>
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.primary' }}>
                                  {logService.formatLogAction(log.acao)}
                                </Typography>
                                <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                  {formatarDataHora(log.createdAt)} {log.usuario?.nome ? `· ${log.usuario.nome}` : ''}
                                </Typography>
                              </Box>
                              <Chip
                                label={logService.formatLogAction(log.acao)}
                                color={logService.getActionColor(log.acao) as any}
                                size="small"
                                sx={{ fontSize: '0.6rem', height: 18, fontWeight: 700 }}
                              />
                            </Box>
                            {log.descricao && (
                              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.5 }}>
                                {log.descricao}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </SectionCard>
                </Grid>
              )}

            </Grid>
          </Fade>
        ) : null}
      </DialogContent>

      {/* ── FOOTER ─────────────────────────────────────────────────────────── */}
      {invoice && (
        <Box
          sx={{
            px: isMobile ? 1.5 : 2,
            py: 1.25,
            borderTop: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          {/* left actions */}
          <Box display="flex" flexWrap="wrap" gap={0.75}>
            {invoice.status !== 'pago' && invoice.status !== 'cancelado' && invoice.status !== 'premiado' && onReceivePayment && (
              <Button
                variant="contained"
                color="success"
                startIcon={<PaymentIcon />}
                onClick={() => onReceivePayment(invoice)}
                size="small"
                sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '0.72rem', px: 1.5 }}
              >
                Receber
              </Button>
            )}

            {invoice.status !== 'cancelado' && invoice.status !== 'pago' && invoice.status !== 'premiado' && (
              <>
                <Button
                  variant="outlined"
                  //onClick={() => onEdit(invoice)}
                  size="small"
                  sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '0.72rem', px: 1.5 }}
                >
                  Editar
                </Button>
                <Button
                  variant="outlined"
                  color="info"
                  startIcon={<SendIcon />}
                  onClick={() => setEnviarModalOpen(true)}
                  size="small"
                  sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '0.72rem', px: 1.5 }}
                >
                  Enviar
                </Button>
              </>
            )}

            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() => { onPrintSingleInvoiceWithSweet?.(invoice); onClose(); }}
              size="small"
              sx={{
                borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '0.72rem', px: 1.5,
                borderColor: theme.palette.info.main, color: theme.palette.info.main,
                '&:hover': { borderColor: theme.palette.info.dark, bgcolor: `${theme.palette.info.main}10` },
              }}
            >
              Imprimir
            </Button>

            {invoice.status !== 'cancelado' && invoice.nfcom_id === null && (
              <Button
                variant="outlined"
                startIcon={<ReceiptLongIcon />}
                onClick={() => onEmitirNfcom?.(invoice)}
                size="small"
                sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '0.72rem', px: 1.5 }}
              >
                NFCom
              </Button>
            )}

            {canProcessBoleto() && invoice.gateway_id_externo === null && onProcessBoleto && (
              <Button
                variant="outlined"
                startIcon={<ReceiptIcon />}
                onClick={() => onProcessBoleto(invoice)}
                size="small"
                sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '0.72rem', px: 1.5 }}
              >
                Processar Boleto
              </Button>
            )}

            {invoice.status !== 'cancelado' && invoice.status !== 'pago' && invoice.status !== 'premiado' && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => {
                  onClose();
                  //onCancelInvoice?.([invoice.id]);
                }}
                size="small"
                sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '0.72rem', px: 1.5 }}
              >
                Cancelar
              </Button>
            )}
          </Box>

          {/* close */}
          <Button
            onClick={onClose}
            variant="text"
            size="small"
            sx={{
              borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '0.72rem',
              color: 'text.secondary', px: 1.5,
              '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' },
            }}
          >
            Fechar
          </Button>
        </Box>
      )}

      {invoice && (
        <EnviarInvoiceModal
          open={enviarModalOpen}
          onClose={() => setEnviarModalOpen(false)}
          invoiceId={invoice.id}
          clientId={invoice.cliente_id}
        />
      )}
    </Dialog>
  );
};

export default InvoiceViewModal;